import { get, type RequestConfig } from "../shared/api-client";
import type {
  ApiItemResponse,
  ApiListResponse,
  ApiPagination,
  ListQuery,
  PaginatedResult,
} from "@project/types/location";

/** Legacy DRF-style list envelope — kept for backward-compatible API responses */
type LegacyPaginatedList<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const JIVANDHARA_BASE = "/jivandhara";
export const SILENT_LIST: RequestConfig = { showNotification: false };

export function isApiListResponse<T>(
  data: unknown
): data is ApiListResponse<T> {
  return (
    typeof data === "object" &&
    data !== null &&
    "data" in data &&
    Array.isArray((data as ApiListResponse<T>).data)
  );
}

export function isApiItemResponse<T>(
  data: unknown
): data is ApiItemResponse<T> {
  return (
    typeof data === "object" &&
    data !== null &&
    "data" in data &&
    "success" in data
  );
}

export function unwrapItem<T>(
  data: ApiItemResponse<T> | T | undefined
): T | undefined {
  if (!data) {
    return undefined;
  }
  if (isApiItemResponse<T>(data)) {
    return data.data;
  }
  return data;
}

const emptyPagination = (count = 0): ApiPagination => ({
  count,
  current_page: 1,
  total_pages: 1,
  next: null,
  previous: null,
});

const inflightRequests = new Map<
  string,
  Promise<PaginatedResult<unknown, unknown>>
>();

export async function fetchListPage<T, S = unknown>(
  path: string,
  query: ListQuery = {}
): Promise<PaginatedResult<T, S>> {
  const {
    page = 1,
    page_size = 20,
    search,
    district,
    city,
    status,
    main_category,
    scheme,
    message,
    donation_type,
    payment_mode,
    item_category_type,
    sponsorships_type,
    english_date,
    ...rest
  } = query;
  const params: Record<string, string | number> = { page, page_size };
  if (search?.trim()) {
    params.search = search.trim();
  }
  if (district) {
    params.district = district;
  }
  if (city) {
    params.city = city;
  }
  if (status?.trim()) {
    params.status = status.trim();
  }
  if (main_category) {
    params.main_category = main_category;
  }
  if (scheme) {
    params.scheme = scheme;
  }
  if (message?.trim()) {
    params.message = message.trim();
  }
  if (donation_type?.trim()) {
    params.donation_type = donation_type.trim();
  }
  if (payment_mode?.trim()) {
    params.payment_mode = payment_mode.trim();
  }
  if (item_category_type?.trim()) {
    params.item_category_type = item_category_type.trim();
  }
  if (sponsorships_type?.trim()) {
    params.sponsorships_type = sponsorships_type.trim();
  }
  if (english_date?.trim()) {
    params.english_date = english_date.trim();
  }

  // Add all other dynamic parameters
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "string") {
        if (value.trim()) {
          params[key] = value.trim();
        }
      } else {
        params[key] = value as string | number;
      }
    }
  });

  const cacheKey = `${path}?${new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)])
  ).toString()}`;

  const existing = inflightRequests.get(cacheKey);
  if (existing) {
    return existing as Promise<PaginatedResult<T, S>>;
  }

  const request = (async () => {
    const response = await get<
      ApiListResponse<T> | LegacyPaginatedList<T> | T[]
    >(path, {
      ...SILENT_LIST,
      params,
    });

    if (isApiListResponse<T>(response)) {
      const listResponse = response as ApiListResponse<T, S>;
      return {
        data: listResponse.data,
        pagination: listResponse.pagination,
        stats: listResponse.stats,
      };
    }

    const list = Array.isArray(response) ? response : (response?.results ?? []);
    return { data: list, pagination: emptyPagination(list.length) };
  })().finally(() => {
    inflightRequests.delete(cacheKey);
  });

  inflightRequests.set(
    cacheKey,
    request as Promise<PaginatedResult<unknown, unknown>>
  );
  return request;
}

const EXPORT_PAGE_SIZE = 100;

/** Walk every page of a list API (current filters) and return the full dataset. */
export async function fetchAllListData<T>(
  list: (query: ListQuery) => Promise<PaginatedResult<T>>,
  query: Omit<ListQuery, "page" | "page_size"> = {},
  pageSize = EXPORT_PAGE_SIZE
): Promise<T[]> {
  const all: T[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const result = await list({
      ...query,
      page,
      page_size: pageSize,
    });
    const chunk = result.data ?? [];
    all.push(...chunk);
    totalPages = Math.max(1, result.pagination?.total_pages ?? 1);
    if (chunk.length === 0) {
      break;
    }
    page += 1;
  } while (page <= totalPages);

  return all;
}
